import React, { useId } from 'react';
import { InterventionLadderStepModel } from '../../../api-service/clients';
import { formatNumber } from '../../../utilities';
import { moveDownColor, moveUpColor, stepColor, stepIntensity, stepMarkerBorders } from '../intervention-ladder/intervention-ladder-colors';

const width = 720;
const lowerLevel = 112;
const selectedLevel = 70;
const higherLevel = 28;
const lowerRiser = 265;
const higherRiser = 455;
const marker = { lower: 150, selected: 360, higher: 570 };
const arrowX = { down: lowerRiser - 15, up: higherRiser - 15 };
const markerHeight = 26;

export function stepRange(steps: InterventionLadderStepModel[]) {
  const first = steps[0].step;
  const last = steps[steps.length - 1].step;
  return first === last ? `${first}` : `${first}–${last}`;
}

export interface InterventionMovesStaircaseProps {
  steps: InterventionLadderStepModel[];
  selectedIndex: number;
  onSelectLower: () => void;
  onSelectHigher: () => void;
}

export function InterventionMovesStaircase({ steps, selectedIndex, onSelectLower, onSelectHigher }: InterventionMovesStaircaseProps) {
  const gradientId = useId();
  const selectedStep = steps[selectedIndex];
  const lowerSteps = steps.slice(0, selectedIndex);
  const higherSteps = steps.slice(selectedIndex + 1);
  const hasLower = lowerSteps.length > 0;
  const hasHigher = higherSteps.length > 0;
  const borderOf = (index: number) => stepColor(stepMarkerBorders, stepIntensity(index, steps.length));
  const stops = [
    ...(hasLower ? [{ x: marker.lower, color: borderOf(selectedIndex - 1) }] : []),
    { x: marker.selected, color: borderOf(selectedIndex) },
    ...(hasHigher ? [{ x: marker.higher, color: borderOf(selectedIndex + 1) }] : []),
  ];
  const path = [hasLower ? `M75,${lowerLevel} H${lowerRiser} V${selectedLevel}` : `M288,${selectedLevel}`, hasHigher ? `H${higherRiser} V${higherLevel} H645` : 'H432'].join(' ');

  return (
    <svg viewBox={`0 0 ${width} 150`} style={{ display: 'block', width: '100%' }} aria-hidden>
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={width} y2="0">
          {stops.map(stop => (
            <stop key={stop.x} offset={stop.x / width} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
      <path d={path} fill="none" stroke={`url(#${gradientId})`} strokeWidth={5} strokeLinejoin="round" strokeLinecap="round" />
      {hasLower && (
        <>
          <MoveArrow x={arrowX.down} from={selectedLevel} to={lowerLevel - 12} color={moveDownColor} />
          <CountChip x={arrowX.down} y={selectedLevel - 16} count={selectedStep.moves.down} color={moveDownColor} />
          <Marker x={marker.lower} y={lowerLevel} label={stepRange(lowerSteps)} border={borderOf(selectedIndex - 1)} onClick={onSelectLower} />
        </>
      )}
      {hasHigher && (
        <>
          <MoveArrow x={arrowX.up} from={selectedLevel - 8} to={higherLevel} color={moveUpColor} />
          <CountChip x={arrowX.up} y={higherLevel - 16} count={selectedStep.moves.up} color={moveUpColor} />
          <Marker x={marker.higher} y={higherLevel} label={stepRange(higherSteps)} border={borderOf(selectedIndex + 1)} onClick={onSelectHigher} />
        </>
      )}
      <circle cx={marker.selected} cy={selectedLevel} r={markerHeight / 2 + 3.5} fill="none" stroke="#1f4d61" strokeWidth={3} />
      <Marker x={marker.selected} y={selectedLevel} label={`${selectedStep.step}`} border={borderOf(selectedIndex)} />
    </svg>
  );
}

interface MarkerProps {
  x: number;
  y: number;
  label: string;
  border: string;
  onClick?: () => void;
}

function Marker({ x, y, label, border, onClick }: MarkerProps) {
  const markerWidth = label.length > 1 ? 46 : markerHeight;
  return (
    <g onClick={onClick} style={onClick && { cursor: 'pointer' }}>
      <rect x={x - markerWidth / 2} y={y - markerHeight / 2} width={markerWidth} height={markerHeight} rx={markerHeight / 2} fill="#ffffff" stroke={border} strokeWidth={2} />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={12.8} fontWeight={700} fill="#1f4d61">
        {label}
      </text>
    </g>
  );
}

interface MoveArrowProps {
  x: number;
  from: number;
  to: number;
  color: string;
}

function MoveArrow({ x, from, to, color }: MoveArrowProps) {
  const base = to - Math.sign(to - from) * 10;
  return (
    <>
      <line x1={x} y1={from} x2={x} y2={base} stroke={color} strokeWidth={7} strokeLinecap="round" />
      <polygon points={`${x - 8},${base} ${x + 8},${base} ${x},${to}`} fill={color} />
    </>
  );
}

interface CountChipProps {
  x: number;
  y: number;
  count: number;
  color: string;
}

function CountChip({ x, y, count, color }: CountChipProps) {
  const label = formatNumber(count);
  const chipWidth = 12 + label.length * 7;
  return (
    <>
      <rect x={x - chipWidth / 2} y={y - 8} width={chipWidth} height={16} rx={8} fill="#ffffff" stroke={color} strokeWidth={1.5} />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700} fill={color}>
        {label}
      </text>
    </>
  );
}
